import type { GetWebhooksQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetWebhooksDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook } from 'src/utils';

import type { WebhookListRow } from './types';

/**
 * Maps the tRPC list payload onto the `GetWebhooks` GraphQL shape consumers
 * already expect. The list endpoint does not join `modifiedByUser` (unused by
 * the table), so it is set to null. The subscription FK (`WebhookEventId`)
 * doubles as the event-type id since it references `organisation_webhook_event_type.Id`.
 */
export const mapWebhooksToGraphQL = (
  data: WebhookListRow[] | undefined
): GetWebhooksQuery => ({
  organisation_webhook: (data ?? []).map((webhook) => ({
    __typename: 'organisation_webhook' as const,
    Id: webhook.Id,
    Title: webhook.Title,
    Description: webhook.Description ?? undefined,
    WebhookUrl: webhook.WebhookUrl,
    WebhookApiVersion: webhook.WebhookApiVersion,
    Status: webhook.Status,
    CreatedAtTimestamp: webhook.CreatedAtTimestamp,
    ModifiedAtTimestamp: webhook.ModifiedAtTimestamp,
    createdByUser: webhook.createdByUser
      ? {
          __typename: 'user' as const,
          FriendlyName: webhook.createdByUser.FriendlyName ?? undefined,
        }
      : null,
    modifiedByUser: null,
    subscriptions: webhook.subscriptions.map((subscription) => ({
      __typename: 'organisation_webhook_subscription' as const,
      WebhookEventId: subscription.WebhookEventId,
      eventType: {
        __typename: 'organisation_webhook_event_type' as const,
        Id: subscription.WebhookEventId,
        EventType: subscription.EventType,
      },
    })),
  })),
});

export const useListWebhooks = createQueryHook<
  undefined,
  WebhookListRow[],
  GetWebhooksQuery
>({
  trpcQueryOptions: (trpc) => trpc.frontend.webhook.list.queryOptions(),
  mapTrpcDataToGraphQL: mapWebhooksToGraphQL,
  graphqlDocument: GetWebhooksDocument,
});
